import React, { useMemo, useState } from 'react';
import { Tags } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { ColorPicker } from '@components/ColorPicker';
import { Switch } from '@components/Switch';
import categoryService from '@services/categoryService';
import { MOVEMENTS, MOVEMENT_LABELS, type Category, type CategoryInput, type Movement } from '@/types/finance';
import { DEFAULT_COLOR } from '@/utils/colors';
import { Required, labelCls } from './financeBits';

interface Props {
  /** Edição. */
  category?: Category | null;
  /** Todas as categorias (para o seletor de pai). */
  categories: Category[];
  /** Criação: movimento da aba atual. */
  movement?: Movement;
  /** Criação de subcategoria: pai pré-selecionado (movimento travado). */
  parent?: Category | null;
  onClose: () => void;
  onSaved: (category: Category) => void;
  onError: (err: unknown) => void;
}

/** Cadastro/edição de categoria (POST/PUT /categories): 2 níveis, filha herda o movimento do pai. */
export const CategoryFormModal: React.FC<Props> = ({ category, categories, movement: initialMovement, parent, onClose, onSaved, onError }) => {
  const isEdit = !!category;
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(category?.name ?? '');
  const [movement, setMovement] = useState<Movement>(category?.movement ?? parent?.movement ?? initialMovement ?? 'expense');
  const [parentId, setParentId] = useState<number | ''>(category?.parent_category_id ?? parent?.id ?? '');
  const [color, setColor] = useState(category?.color ?? parent?.color ?? DEFAULT_COLOR);
  const [icon, setIcon] = useState(category?.icon ?? '');
  const [disabled, setDisabled] = useState(category?.is_disabled ?? false);

  const isSystem = !!category?.is_system;
  // Uma categoria que já tem filhas não pode virar filha (só 1 nível).
  const hasChildren = useMemo(() => !!category && categories.some((c) => c.parent_category_id === category.id), [category, categories]);
  const movementLocked = isSystem || parentId !== '' || (isEdit && (category?.transaction_count ?? 0) > 0);
  const parentLocked = isSystem || hasChildren;

  const parentOptions = useMemo(
    () => categories.filter((c) => c.movement === movement && !c.parent_category_id && c.id !== category?.id && !c.is_disabled),
    [categories, movement, category?.id],
  );

  const changeMovement = (m: Movement) => {
    setMovement(m);
    setParentId('');
  };

  const changeParent = (id: number | '') => {
    setParentId(id);
    const p = id ? categories.find((c) => c.id === id) : undefined;
    if (p) setMovement(p.movement);
  };

  const canSubmit = !saving && !!name.trim();

  const save = async () => {
    if (!canSubmit) return;
    const payload: CategoryInput = {
      name: name.trim(),
      movement,
      parent_category_id: parentId === '' ? null : parentId,
      icon: icon.trim() || null,
      color,
      sort_order: category?.sort_order ?? null,
      is_disabled: disabled,
    };
    setSaving(true);
    try {
      onSaved(category ? await categoryService.update(category.id, payload) : await categoryService.create(payload));
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="md" closeOnBackdrop={!saving}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <Tags size={18} className="text-slate-500" />
        <h2 className="text-h3">{isEdit ? 'Editar categoria' : parent ? `Subcategoria de ${parent.name}` : 'Nova categoria'}</h2>
      </div>
      <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-3">
        {isSystem && <p className="text-small rounded-lg bg-slate-50 border border-slate-200 p-2">Categoria de sistema: só nome, cor, ícone e ordem podem mudar; não pode ser excluída (use "desativada").</p>}
        <div>
          <label className={labelCls}>
            Nome
            <Required />
          </label>
          <input className="input-field" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>
              Movimento
              <Required />
            </label>
            <select className="select-field" value={movement} disabled={movementLocked} onChange={(e) => changeMovement(e.target.value as Movement)}>
              {MOVEMENTS.map((m) => (
                <option key={m} value={m}>
                  {MOVEMENT_LABELS[m]}
                </option>
              ))}
            </select>
            {movementLocked && !isSystem && <p className="text-small mt-1">{parentId !== '' ? 'Subcategoria herda o movimento do pai.' : 'Categoria com lançamentos não muda de movimento.'}</p>}
          </div>
          <div>
            <label className={labelCls}>Categoria pai</label>
            <select className="select-field" value={parentId} disabled={parentLocked} onChange={(e) => changeParent(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Nenhuma (categoria principal)</option>
              {parentOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {hasChildren && <p className="text-small mt-1">Tem subcategorias: continua como principal.</p>}
          </div>
        </div>
        <div>
          <label className={labelCls}>Cor</label>
          <ColorPicker value={color} onChange={setColor} />
        </div>
        <div>
          <label className={labelCls}>Ícone (opcional)</label>
          <input className="input-field" maxLength={50} value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="nome do ícone lucide, ex.: home, car, utensils" />
        </div>
        <Switch checked={disabled} onChange={setDisabled} label="Desativada (não aparece nos seletores de lançamento)" />
      </div>
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={save} loading={saving} disabled={!canSubmit}>
          {isEdit ? 'Salvar' : 'Criar'}
        </Button>
      </div>
    </Modal>
  );
};
